/**
 * RangePool - Object pool for DOM Range instances
 *
 * Why this exists: The StressOverlayPlugin creates a new Range for every syllable
 * during recalculation. On scroll/resize, this triggers many allocations per frame,
 * creating GC pressure that can cause jank. Pooling Ranges amortizes allocation
 * cost and reduces GC pauses.
 *
 * Usage:
 *   const pool = new RangePool()
 *   const range = pool.acquire()
 *   range.setStart(textNode, 0)
 *   range.setEnd(textNode, 5)
 *   const rect = range.getBoundingClientRect()
 *   pool.release(range)
 *
 * Lifecycle:
 *   - acquire() returns a Range from the pool (or creates new if pool empty)
 *   - release() detaches the Range and returns it to the pool
 *   - clear() detaches all pooled Ranges (call on unmount)
 */

export class RangePool {
  private pool: Range[] = []
  private readonly maxSize: number

  /**
   * Create a new RangePool
   * @param maxSize - Maximum Ranges to keep in pool (prevents unbounded growth)
   */
  constructor(maxSize: number = 50) {
    this.maxSize = maxSize
  }

  /**
   * Get a Range from the pool, or create a new one if pool is empty
   */
  acquire(): Range {
    if (this.pool.length > 0) {
      return this.pool.pop()!
    }
    return document.createRange()
  }

  /**
   * Return a Range to the pool for reuse
   * Detaches the Range to avoid holding references to DOM nodes
   */
  release(range: Range): void {
    // Detach before returning to pool - prevents memory leaks from retained node refs
    try {
      range.detach()
    } catch {
      // Range.detach() is a no-op in modern browsers but may throw in older ones
      // Swallow errors - we're just being defensive
    }

    // Only keep up to maxSize Ranges in the pool
    if (this.pool.length < this.maxSize) {
      this.pool.push(range)
    }
    // If pool is full, let the Range get GC'd
  }

  /**
   * Clear the pool and detach all Ranges
   * Call this on component unmount to clean up
   */
  clear(): void {
    for (const range of this.pool) {
      try {
        range.detach()
      } catch {
        // Swallow - defensive cleanup
      }
    }
    this.pool = []
  }

  /**
   * Current number of Ranges in the pool
   * Useful for testing/debugging
   */
  get size(): number {
    return this.pool.length
  }
}

export default RangePool
